import React, { useState, useEffect, Suspense } from "react";
import classNames from "classnames";
import { Route, Switch } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./App.scss";

import AppTopbar from "./layout/AppTopbar/AppTopbar";
import { Error } from "./pages/Error/Error";
import { Loading } from "./pages/Loading/Loading";

import { getRoute } from "./helper/customRoute";
import { ROUTE_CONST } from "./constants/actiontypes";
import DeleteCartConfirmation from "./pages/DeleteCartConfirmation/DeleteCartConfirmation";

import { getName, getUsername } from "./reducers/login";
import { useSelector } from "react-redux";
import { PageNotFound } from "./pages/PageNotFound/PageNotFound";
import { getCurrentTrader } from "./reducers/trader";
import Invitation from "./pages/Account/Invitation/Invitation";
import { useAppThunkDispatch } from "./store/store";

const Traders = React.lazy(() => import("./pages/Traders/Traders"));
const Items = React.lazy(() => import("./pages/Items/Items"));
const Cart = React.lazy(() => import("./pages/Cart/Cart"));
const ItemDetail = React.lazy(() => import("./pages/ItemDetail/ItemDetail"));
const CartDefaultSelection = React.lazy(
  () => import("./pages/CartDefaultSelection/CartDefaultSelection")
);
const OrderConfirmation = React.lazy(
  () => import("./pages/OrderConfirmation/OrderConfirmation")
);
const StandingOrder = React.lazy(
  () => import("./pages/StandingOrder/StandingOrder")
);
const StandingOrderItems = React.lazy(
  () => import("./pages/StandingOrderItems/StandingOrderItems")
);

const AddStandingOrder = React.lazy(
  () => import("./pages/ItemsAddStandingOrder/ItemsAddStandingOrder")
);

const StandingOrderSummary = React.lazy(
  () => import("./pages/StandingOrderSummary/StandingOrderSummary")
);

type Props = {
  showCustomers: boolean | null;
  clientName: string | null;
  clientFullName: string | null;
  setToken: any;
  setAppLoadingStatus: any;
  onSetLanguage: (lang: string) => void;
  currentLang: string;
  logout: () => void;
};

const App: React.FC<Props> = (props: Props) => {
  const dispatch = useAppThunkDispatch();

  const { t } = useTranslation("common");
  const [staticMenuMobileActive, setStaticMenuMobileActive] = useState(false);
  const [sidebarStatic, setSidebarStatic] = useState(false);
  const [sidebarActive, setSidebarActive] = useState(false);
  const [menuActive, setMenuActive] = useState(false);
  const [menuMode, setMenuMode] = useState("sidebar");
  const [configActive, setConfigActive] = useState(false);
  const [inputStyle, setInputStyle] = useState("filled");
  const [ripple, setRipple] = useState(false);
  const [rightPanelActive, setRightPanelActive] = useState(false);

  const [topbarScheme, setTopbarScheme] = useState<string | null>("light");
  const [menuScheme, setMenuScheme] = useState("light");

  const [searchActive, setSearchActive] = useState(false);
  const [topbarUserMenuActive, setTopbarUserMenuActive] = useState(false);
  const [show_customers, setShowCustomers] = useState<boolean | null>(null);

  const infoName = useSelector(getName);
  const infoUsername = useSelector(getUsername);
  const trader = useSelector(getCurrentTrader);

  const menu = [
    {
      label: `${t("Global_Home")}`,
      icon: "pi pi-home",
      to: "/",
    },
  ];

  let menuClick: any;
  let rightPanelClick: any;
  let configClick: any;
  let searchClick: any;
  let topbarUserMenuClick: any;

  useEffect(() => {
    setShowCustomers(props.showCustomers);
  }, [props.showCustomers]);

  useEffect(() => {
    if (staticMenuMobileActive) {
      blockBodyScroll();
    } else {
      unblockBodyScroll();
    }
  }, [staticMenuMobileActive]);

  useEffect(() => {
    setMenuActive(false);
  }, [menuMode]);

  useEffect(() => {
    const myElement1: any = document.getElementById("txtTraderName");
    if (myElement1) {
      myElement1.dispatchEvent(new Event("change"));
    }
  }, [trader?.name, trader?.id]);

  function blockBodyScroll() {
    if (document.body.classList) {
      document.body.classList.add("blocked-scroll");
    } else {
      document.body.className += " blocked-scroll";
    }
  }

  const unblockBodyScroll = () => {
    if (document.body.classList) {
      document.body.classList.remove("blocked-scroll");
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          "(^|\\b)" + "blocked-scroll".split(" ").join("|") + "(\\b|$)",
          "gi"
        ),
        " "
      );
    }
  };

  const isHorizontal = () => {
    return menuMode === "horizontal";
  };

  const isSlim = () => {
    return menuMode === "slim";
  };

  const hideOverlayMenu = () => {
    setStaticMenuMobileActive(false);
    unblockBodyScroll();
  };

  const onDocumentClick = () => {
    if (!searchClick && searchActive) {
      setSearchActive(false);
      searchClick = false;
    }

    if (!topbarUserMenuClick && topbarUserMenuActive) {
      setTopbarUserMenuActive(false);
      topbarUserMenuClick = false;
    }

    if (!rightPanelClick && rightPanelActive) {
      setRightPanelActive(false);
    }

    if (!configClick && configActive) {
      setConfigActive(false);
    }

    if (!menuClick) {
      if (isSlim() || isHorizontal()) {
        setMenuActive(false);
      }

      if (staticMenuMobileActive) {
        hideOverlayMenu();
      }

      unblockBodyScroll();
    }

    searchClick = false;
    topbarUserMenuClick = false;
    rightPanelClick = false;
    configClick = false;
    menuClick = false;
  };

  const onSetLanguage = (lang: string) => {
    props.onSetLanguage(lang);
  };

  const layoutClassName = classNames(
    "layout-wrapper",
    {
      "layout-sidebar": menuMode === "sidebar",
      "layout-static": menuMode === "sidebar" && sidebarStatic,
      "layout-horizontal": menuMode === "horizontal",
      "layout-rightpanel-active": rightPanelActive,
      "layout-slim": menuMode === "slim",
      "layout-mobile-active": staticMenuMobileActive,
      "p-input-filled": inputStyle === "filled",
      "p-ripple-disabled": !ripple,
    },
    "layout-menu-" + menuScheme + " layout-topbar-" + topbarScheme
  );

  return (
    <div className={layoutClassName} onClick={onDocumentClick}>
      <AppTopbar
        onSetLanguage={onSetLanguage}
        currentlang={props.currentLang}
        showCustomers={show_customers}
        clientFullName={props.clientFullName}
        clientName={props.clientName}
        setAppLoadingStatus={props.setAppLoadingStatus}
        setToken={props.setToken}
        logout={props.logout}
      />

      <div className="layout-main">
        <div className="layout-content">
          {show_customers === true ? (
            <Suspense
              fallback={
                <div>
                  <Loading></Loading>
                </div>
              }
            >
              <Switch>
                <Route
                  path={getRoute(ROUTE_CONST.CUSTOMERS, props.clientName || "")}
                  exact
                  component={Traders}
                />
                <Route
                  path={getRoute(ROUTE_CONST.LOGIN, props.clientName || "")}
                  exact
                  component={Traders}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.INVITATION,
                    props.clientName || ""
                  )}
                  exact
                  component={Invitation}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.CARTDEFSELECTION,
                    props.clientName || ""
                  )}
                  exact
                  component={CartDefaultSelection}
                />
                <Route
                  path={getRoute(ROUTE_CONST.ITEMS, props.clientName || "")}
                  exact
                  component={Items}
                />

                <Route
                  path={getRoute(
                    ROUTE_CONST.ITEMDETAIL,
                    props.clientName || ""
                  )}
                  exact
                  component={ItemDetail}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.ORDERCONFIRMATION,
                    props.clientName || ""
                  )}
                  exact
                  component={OrderConfirmation}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.DELETEORDERCONFIRMATION,
                    props.clientName || ""
                  )}
                  exact
                  component={DeleteCartConfirmation}
                />
                <Route
                  path={getRoute(ROUTE_CONST.CART, props.clientName || "")}
                  exact
                  component={Cart}
                />
                <Route
                  path={getRoute(ROUTE_CONST.ERROR, props.clientName || "")}
                  exact
                  component={Error}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.PAGENOTFOUND,
                    props.clientName || ""
                  )}
                  exact
                  component={PageNotFound}
                />

                <Route
                  path={getRoute(
                    ROUTE_CONST.STANDINGORDER,
                    props.clientName || ""
                  )}
                  exact
                  component={StandingOrder}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.STANDINGORDERITEMS,
                    props.clientName || ""
                  )}
                  exact
                  component={StandingOrderItems}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.ADDSTANDINGORDER,
                    props.clientName || ""
                  )}
                  exact
                  component={AddStandingOrder}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.STANDINGORDERSUMMARY,
                    props.clientName || ""
                  )}
                  exact
                  component={StandingOrderSummary}
                />
                <Route path="*" exact={true} component={Traders} />
              </Switch>
            </Suspense>
          ) : (
            <Suspense
              fallback={
                <div>
                  <Loading></Loading>
                </div>
              }
            >
              <Switch>
                <Route
                  path={getRoute(
                    ROUTE_CONST.CARTDEFSELECTIONHOME,
                    props.clientName || ""
                  )}
                  exact
                  component={CartDefaultSelection}
                />
                <Route
                  path={getRoute(ROUTE_CONST.LOGIN, props.clientName || "")}
                  exact
                  component={CartDefaultSelection}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.INVITATION,
                    props.clientName || ""
                  )}
                  exact
                  component={Invitation}
                />
                <Route
                  path={getRoute(ROUTE_CONST.ITEMS, props.clientName || "")}
                  exact
                  component={Items}
                />

                <Route
                  path={getRoute(
                    ROUTE_CONST.ITEMDETAIL,
                    props.clientName || ""
                  )}
                  exact
                  component={ItemDetail}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.ORDERCONFIRMATION,
                    props.clientName || ""
                  )}
                  exact
                  component={OrderConfirmation}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.DELETEORDERCONFIRMATION,
                    props.clientName || ""
                  )}
                  exact
                  component={DeleteCartConfirmation}
                />
                <Route
                  path={getRoute(ROUTE_CONST.CART, props.clientName || "")}
                  exact
                  component={Cart}
                />
                <Route
                  path={getRoute(ROUTE_CONST.ERROR, props.clientName || "")}
                  exact
                  component={Error}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.PAGENOTFOUND,
                    props.clientName || ""
                  )}
                  exact
                  component={PageNotFound}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.STANDINGORDER,
                    props.clientName || ""
                  )}
                  exact
                  component={StandingOrder}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.STANDINGORDERITEMS,
                    props.clientName || ""
                  )}
                  exact
                  component={StandingOrderItems}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.ADDSTANDINGORDER,
                    props.clientName || ""
                  )}
                  exact
                  component={AddStandingOrder}
                />
                <Route
                  path={getRoute(
                    ROUTE_CONST.STANDINGORDERSUMMARY,
                    props.clientName || ""
                  )}
                  exact
                  component={StandingOrderSummary}
                />
                <Route path="*" exact={true} component={CartDefaultSelection} />
              </Switch>
            </Suspense>
          )}
        </div>
      </div>
      <div className="layout-mask modal-in"></div>
      <input
        style={{ display: "none" }}
        id="txtName"
        value={infoName}
        readOnly
      ></input>
      <input
        style={{ display: "none" }}
        id="txtEmail"
        value={infoUsername}
        readOnly
      ></input>
      <input
        style={{ display: "none" }}
        id="txtTraderName"
        value={trader?.name || ""}
        readOnly
      ></input>
      <input
        style={{ display: "none" }}
        id="txtTraderId"
        value={trader?.id || ""}
        readOnly
      ></input>
    </div>
  );
};

export default App;
